import React from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { ArrowRight, Triangle, Ruler, Calculator, ClockCounterClockwise, Receipt, CalendarBlank, ChartLineUp, Camera, FileText } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { user } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [params] = useSearchParams();
  const unauthorized = params.get("error") === "unauthorized";

  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/app/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  React.useEffect(() => {
    if (user) window.location.href = "/app/dashboard";
  }, [user]);

  const features = [
    { icon: Ruler, en: "TAKEOFF", fr: "MÉTRÉ" },
    { icon: Calculator, en: "ESTIMATING", fr: "ESTIMATION" },
    { icon: FileText, en: "PROPOSALS", fr: "SOUMISSIONS" },
    { icon: Camera, en: "PHOTOS", fr: "PHOTOS" },
    { icon: ClockCounterClockwise, en: "TIMESHEET", fr: "FEUILLE T." },
    { icon: Receipt, en: "BILLING", fr: "FACTURATION" },
    { icon: CalendarBlank, en: "CALENDAR", fr: "CALENDRIER" },
    { icon: ChartLineUp, en: "ACCOUNTING", fr: "COMPTA." },
  ];

  const headlineEn = ["From shingle count", "to paid invoice."];
  const headlineFr = ["Du bardeau", "à la facture payée."];

  return (
    <div className="min-h-screen w-full bg-white text-zinc-900 grid grid-cols-1 lg:grid-cols-5">
      {/* LEFT */}
      <div className="lg:col-span-3 relative flex flex-col justify-between p-8 sm:p-12 lg:p-16 border-r border-zinc-200 grid-paper">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-orange-600 flex items-center justify-center">
              <Triangle size={18} weight="fill" className="text-white" />
            </div>
            <div>
              <div className="font-display font-black tracking-tight text-base leading-none">C.H.C.</div>
              <div className="label-eyebrow text-[9px] text-zinc-500 mt-0.5">LES ENTREPRISES</div>
            </div>
          </div>
          <LangToggle lang={lang} setLang={setLang} />
        </div>

        <div className="max-w-2xl">
          <p className="label-eyebrow mb-6">v1.0 · {t("brand.tagline")}</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[0.95]">
            {(lang === "fr" ? headlineFr : headlineEn)[0]}<br />
            <span className="text-orange-600">{(lang === "fr" ? headlineFr : headlineEn)[1]}</span>
          </h1>
          <p className="mt-8 text-zinc-600 text-base sm:text-lg max-w-xl leading-relaxed">
            {lang === "fr"
              ? "Métré, estimation, planification, suivi des heures, facturation et comptabilité — un seul outil pensé pour la façon dont vous gérez réellement vos chantiers."
              : "Takeoff, estimate, schedule, time-track, invoice and book the keep. A single, dense control room built for the way you actually run jobs."}
          </p>

          <div className="mt-12">
            <Button
              data-testid="google-login-button"
              onClick={handleLogin}
              size="lg"
              className="rounded-none bg-zinc-900 hover:bg-orange-600 text-white font-bold tracking-wide uppercase px-8 h-14 text-sm transition-colors duration-200"
            >
              {t("auth.signin")}
              <ArrowRight size={18} weight="bold" className="ml-3" />
            </Button>
            <p className="mt-4 text-xs text-zinc-500 mono">{t("auth.secure")}</p>
            {unauthorized && (
              <p data-testid="auth-error" className="mt-4 text-xs mono text-red-600 border-l-2 border-red-600 pl-3">
                {t("auth.unauthorized")}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-4 sm:grid-cols-8 gap-px bg-zinc-200 border border-zinc-200">
          {features.map((f) => (
            <div key={f.en} className="bg-white p-3 flex flex-col items-start gap-1.5">
              <f.icon size={18} className="text-orange-600" weight="duotone" />
              <span className="label-eyebrow text-[9px] text-zinc-700">{lang === "fr" ? f.fr : f.en}</span>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT */}
      <div className="lg:col-span-2 bg-zinc-950 text-zinc-100 p-8 sm:p-12 lg:p-16 flex flex-col justify-between">
        <div>
          <p className="label-eyebrow text-orange-500 mb-8">// {lang === "fr" ? "EN DIRECT" : "LIVE NUMBERS"}</p>
          <div className="space-y-10">
            <StatRow k="01" big="2,400" unit="SQ FT" label={lang === "fr" ? "Métré moyen d'un toit" : "Average roof takeoff"} />
            <StatRow k="02" big="24" unit="SEC" label={lang === "fr" ? "De l'estimation à la facture" : "Estimate to invoice"} />
            <StatRow k="03" big="0" unit={lang === "fr" ? "TABLEURS" : "SPREADSHEETS"} label={lang === "fr" ? "Requis" : "Required"} />
          </div>
        </div>
        <div className="text-xs text-zinc-500 mono leading-relaxed">
          MONTRÉAL · QC · CA<br />
          BUILD #2026.02 · C.H.C. OPS CONSOLE
        </div>
      </div>
    </div>
  );
}

function LangToggle({ lang, setLang }) {
  return (
    <div className="border border-zinc-300 inline-flex">
      {["en", "fr"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`label-eyebrow px-3 py-1.5 transition-colors ${lang === l ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-100"}`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function StatRow({ k, big, unit, label }) {
  return (
    <div className="border-l-2 border-orange-600 pl-5">
      <div className="mono text-xs text-zinc-500 mb-2">{k}</div>
      <div className="flex items-baseline gap-3">
        <span className="font-display font-black text-5xl sm:text-6xl tracking-tighter">{big}</span>
        <span className="label-eyebrow text-zinc-400">{unit}</span>
      </div>
      <div className="text-sm text-zinc-400 mt-2">{label}</div>
    </div>
  );
}
